import { ConsumerFilters } from "@features/cx/consumerManagement/types";
import { ActionType, EntityType, ModuleType } from "../../queryKeyFactory";
import { ServiceOrderFilters } from "@features/cx/shared/types/serviceOrdersTabProps";
import { moduleType } from "@rolldown/pluginutils";

// ================================
// CONSUMER KEY FACTORIES
// ================================

/**
 * Consumer key factory
 */
export const consumerKeyFactory = {
  // ================================
  // CONSUMER LIST
  // ================================
  consumerSearch: (
    searchTerm: string,
    remoteUtilityId?: number,
  ): [ModuleType, EntityType, ActionType, string, number] => [
    "cx",
    "consumer",
    "search",
    searchTerm,
    remoteUtilityId,
  ],
  consumerStatus: (): [ModuleType, EntityType, ActionType] => [
    "cx",
    "consumer",
    "status",
  ],
  consumerCategory: (): [ModuleType, EntityType, ActionType] => [
    "cx",
    "consumer",
    "category",
  ],
  nonMeterServices: (
    consumer_id: string,
    remoteUtilityId?: number,
  ): [ModuleType, EntityType, ActionType, string, number] => [
    "cx",
    "consumer",
    "nonMeterServices",
    consumer_id,
    remoteUtilityId,
  ],
  consumerMetadata: (
    consumer_id: string,
    remoteUtilityId?: number,
  ): [ModuleType, EntityType, ActionType, string, number] => [
    "cx",
    "consumer",
    "metadata",
    consumer_id,
    remoteUtilityId,
  ],
  consumerList: (
    filters?: ConsumerFilters,
  ): [ModuleType, EntityType, ActionType, ConsumerFilters] => [
    "cx",
    "consumer",
    "list",
    filters,
  ],
  consumerExport: (
    filters?: ConsumerFilters,
  ): [ModuleType, EntityType, ActionType, ConsumerFilters] => [
    "cx",
    "consumer",
    "consumerExport",
    filters,
  ],

  contract: (
    remoteUtilityId: any,
    contractId?: any,
  ): [ModuleType, EntityType, ActionType, any, any] => [
    "cx",
    "consumer",
    "contract",
    remoteUtilityId,
    contractId,
  ],

  getContractsDuringAddList: (
    params: {
      remoteUtilityId: string;
      extractContractIds: string[];
      subcategories: string[];
    },
  ): [ModuleType, EntityType, ActionType, typeof params] => [
    "cx",
    "consumer",
    "contract",
    params,
  ],
  // ================================
  // CONSUMER DETAILS
  // ================================
  consumerDetails: (
    id: string,
  ): [ModuleType, EntityType, ActionType, string] => [
    "cx",
    "consumer",
    "details",
    id,
  ],
  consumerBillData: (
    id: string,
  ): [ModuleType, EntityType, ActionType, string] => [
    "cx",
    "consumer",
    "billData",
    id,
  ],
  consumerActivity: (
    id: string,
    filters: any = {},
  ): [ModuleType, EntityType, ActionType, string, any] => [
    "cx",
    "consumer",
    "activity",
    id,
    filters,
  ],
  consumerDocuments: (
    id: string,
  ): [ModuleType, EntityType, ActionType, string] => [
    "cx",
    "consumer",
    "documents",
    id,
  ],
  consumerNotes: (id: string): [ModuleType, EntityType, ActionType, string] => [
    "cx",
    "consumer",
    "notes",
    id,
  ],
  consumerServiceOrders: (
    id: string,
    filters: ServiceOrderFilters = {},
  ): [ModuleType, EntityType, ActionType, string, any] => [
    "cx",
    "consumer",
    "serviceOrders",
    id,
    filters,
  ],
  consumerFinancialSummary: (
    id: string,
  ): [ModuleType, EntityType, ActionType, string] => [
    "cx",
    "consumer",
    "financialSummary",
    id,
  ],
  consumerBillingHistory: (
    id: string,
    filters: any,
  ): [ModuleType, EntityType, ActionType, string, any] => [
    "cx",
    "consumer",
    "billingHistory",
    id,
    filters,
  ],

  consumerConnectionHistory: (
    id: string,
    filters: any,
  ): [ModuleType, EntityType, ActionType, string, any] => [
    "cx",
    "consumer",
    "paymentHistory",
    id,
    filters,
  ],

  consumerPaymentHistory: (
    id: string,
    filters: any,
  ): [ModuleType, EntityType, ActionType, string, any] => [
    "cx",
    "consumer",
    "paymentHistory",
    id,
    filters,
  ],
  consumerConsumptionData: (
    id: string,
    period: string,
  ): [ModuleType, EntityType, ActionType, string, string] => [
    "cx",
    "consumer",
    "consumptionData",
    id,
    period,
  ],
  consumerRelationshipInsights: (
    id: string,
  ): [ModuleType, EntityType, ActionType, string] => [
    "cx",
    "consumer",
    "relationshipInsights",
    id,
  ],
  consumerUtilityConnections: (
    id: string,
  ): [ModuleType, EntityType, ActionType, string] => [
    "cx",
    "consumer",
    "utilityConnections",
    id,
  ],
  consumerConnectionsHistory: (
    id: string,
  ): [ModuleType, EntityType, ActionType, string] => [
    "cx",
    "consumer",
    "connectionsHistory",
    id,
  ],
  consumerMeterReadings: (
    consumerId: string,
    meterId: string,
  ): [ModuleType, EntityType, ActionType, string, string] => [
    "cx",
    "consumer",
    "meterReadings",
    consumerId,
    meterId,
  ],
  consumerComplaints: (
    id: string,
  ): [ModuleType, EntityType, ActionType, string] => [
    "cx",
    "consumer",
    "complaints",
    id,
  ],
  consumerServiceRequests: (
    id: string,
  ): [ModuleType, EntityType, ActionType, string] => [
    "cx",
    "consumer",
    "serviceRequests",
    id,
  ],
  consumerCommunications: (
    id: string,
    filters: any = {},
  ): [ModuleType, EntityType, ActionType, string, any] => [
    "cx",
    "consumer",
    "communications",
    id,
    filters,
  ],
  consumerSummary: (
    remote_utility_id: number,
  ): [ModuleType, EntityType, ActionType, number] => [
    "cx",
    "consumer",
    "dashboard",
    remote_utility_id,
  ],
  channelType: (): [ModuleType, EntityType, ActionType] => [
    "cx",
    "consumer",
    "channelType",
  ],
  channelTemplates: (
    channelType: string,
  ): [ModuleType, EntityType, ActionType, string] => [
    "cx",
    "consumer",
    "channelTemplates",
    channelType,
  ],
  category: (): [ModuleType, EntityType, ActionType] => [
    "cx",
    "consumer",
    "category",
  ],
  subCategory: (
    categoryCode: string,
  ): [ModuleType, EntityType, ActionType, string] => [
    "cx",
    "consumer",
    "subCategory",
    categoryCode,
  ],
  verifyEmail: (
    email: string,
    remoteUtilityId: number,
  ): [ModuleType, EntityType, ActionType, string, number] => [
    "cx",
    "consumer",
    "verifyEmail",
    email,
    remoteUtilityId,
  ],
  paymentChannelChoices: (
    remoteUtilityId: number,
  ): [ModuleType, EntityType, ActionType, number] => [
    "cx",
    "consumer",
    "paymentChannel",
    remoteUtilityId,
  ],
  paymentPayTypeChoices: (
    remoteUtilityId: number,
  ): [ModuleType, EntityType, ActionType, number] => [
    "cx",
    "consumer",
    "payTypeChoices",
    remoteUtilityId,
  ],
  paymentMethod: (
    remoteUtilityId: number,
  ): [ModuleType, EntityType, ActionType, number] => [
    "cx",
    "consumer",
    "paymentMethod",
    remoteUtilityId,
  ],
  paymentAgreements: (
    remoteUtilityId: number,
  ): [ModuleType, EntityType, ActionType, number] => [
    "cx",
    "consumer",
    "paymentAgreements",
    remoteUtilityId,
  ],
  planDetails: (
    remoteUtilityId: number,
    planId: string,
  ): [ModuleType, EntityType, ActionType, number, string] => [
    "cx",
    "consumer",
    "planDetails",
    remoteUtilityId,
    planId,
  ],
  city: (): [ModuleType, EntityType, "cities"] => ["cx", "consumer", "cities"],
  subAccountsList: (filters: any): [ModuleType, EntityType, ActionType, any] => [
    "cx",
    "consumer",
    "subAccountsList",
    filters,
  ],
  consumerPlanConfig: (
    consumerId: string,
    remoteUtilityId: number | string,
  ): [ModuleType, EntityType, ActionType, string, number | string] => [
    "cx",
    "consumer",
    "consumerPlanConfig",
    consumerId,
    remoteUtilityId,
  ],
};

